import React, { useCallback } from "react";
import CustomButton from "../CustomUi/CustomButton";
import { Box, Link, Text, VStack } from "@chakra-ui/react";
import CustomCheckBox from "../CustomUi/CustomCheckBox";
import { RegistrationDto } from "../../services/Dtos";
import useThemeColors from "../../hooks/useThemeColors";
import { useFormValidation } from "../../hooks/useFormValidation";
import { useFormChanges } from "../../hooks/useFormChanges";
import FormNavigationLinks from "../CustomUi/FormNavigationLinks";
import useIcons from "../../hooks/useIcons";
import CustomAlert from "../CustomUi/CustomAlert";

interface Props {
  loading: boolean;
  registrationData: RegistrationDto;
  setRegistrationData: React.Dispatch<React.SetStateAction<RegistrationDto>>;
  handleRegister: (e: React.FormEvent) => Promise<void>;
  handlePreviousStep: () => void;
  error?: string[];
}

function TermsAgreementForm({
  loading,
  registrationData,
  setRegistrationData,
  handleRegister,
  handlePreviousStep,
  error = [],
}: Props) {
  const colors = useThemeColors();
  const icons = useIcons();
  // Form field configuration for validation
  const formFields = {
    agreeToTerms: { type: "boolean" as const, required: true },
    agreeToPrivacyPolicy: { type: "boolean" as const, required: true },
  };

  // Form validation hook
  const { errors, clearErrors, setFieldError } = useFormValidation();

  // Form changes hook
  const { hasChanges, validateAllFields, hasValidationErrors } = useFormChanges(
    {
      fields: formFields,
      currentData: registrationData,
      isEditMode: false,
    }
  );

  // Handle checkbox changes
  const handleCheckboxChange = useCallback(
    (
      field: keyof Pick<
        RegistrationDto,
        "agreeToTerms" | "agreeToPrivacyPolicy"
      >
    ) => {
      setRegistrationData((prev) => ({
        ...prev,
        [field]: !prev[field],
      }));

      // Clear field error when user makes a selection
      if (errors[field]) {
        setFieldError(field, "");
      }
    },
    [setRegistrationData, errors, setFieldError]
  );

  // Handle form submission with validation
  const handleFormSubmit = useCallback(
    async (e: React.FormEvent) => {
      clearErrors();

      // Validate all fields
      const validationErrors = validateAllFields();

      if (hasValidationErrors(validationErrors)) {
        // Set errors for display
        Object.entries(validationErrors).forEach(([field, error]) => {
          if (error) setFieldError(field, error);
        });
        return;
      }

      // If validation passes, proceed with registration
      handleRegister(e);
    },
    [
      clearErrors,
      validateAllFields,
      hasValidationErrors,
      setFieldError,
      handleRegister,
    ]
  );

  return (
    <VStack gap={6} align="stretch">
      {/* Display API errors if any */}
      {error.length > 0 && (
        <VStack gap={2} align="stretch">
          {error.slice(0, 3).map((errorMessage, index) => (
            <CustomAlert key={index} message={errorMessage} tight />
          ))}
        </VStack>
      )}

      <Box>
        <Text fontSize="sm" mb={6}>
          Before completing your registration, please review and agree to our
          Terms of Service and Privacy Policy.
        </Text>

        <VStack gap={4} align="stretch">
          <CustomCheckBox
            size="xs"
            checked={registrationData.agreeToTerms}
            onChange={() => handleCheckboxChange("agreeToTerms")}
            label={
              <Text fontSize="sm">
                I agree to the{" "}
                <Link color="blue.500" href="/terms" target="_blank">
                  Terms of Service
                </Link>
              </Text>
            }
          />

          <CustomCheckBox
            size="xs"
            checked={registrationData.agreeToPrivacyPolicy}
            onChange={() => handleCheckboxChange("agreeToPrivacyPolicy")}
            label={
              <Text fontSize="sm">
                I agree to the{" "}
                <Link color="blue.500" href="/privacy" target="_blank">
                  Privacy Policy
                </Link>
              </Text>
            }
          />
        </VStack>

        {/* Display validation errors */}
        {(errors.agreeToTerms || errors.agreeToPrivacyPolicy) && (
          <VStack gap={2} mt={2} align="stretch">
            {errors.agreeToTerms && (
              <Text fontSize="xs" color="red.500">
                {errors.agreeToTerms}
              </Text>
            )}
            {errors.agreeToPrivacyPolicy && (
              <Text fontSize="xs" color="red.500">
                {errors.agreeToPrivacyPolicy}
              </Text>
            )}
          </VStack>
        )}

        <Text fontSize="xs" mt={4} opacity={0.5}>
          By checking both boxes, you acknowledge that you have read,
          understood, and agree to be bound by our Terms of Service and Privacy
          Policy.
        </Text>
      </Box>

      <CustomButton
        label="Complete Registration"
        onClick={handleFormSubmit}
        loading={loading}
        disabled={
          !registrationData.agreeToTerms ||
          !registrationData.agreeToPrivacyPolicy ||
          loading ||
          !hasChanges
        }
        colorPalette={colors.brand.primary}
      />
      <FormNavigationLinks
        links={[
          {
            text: "Go Back",
            onClick: handlePreviousStep,
            icon: icons.utility.arrowLeft,
            isDisabled: loading,
          },
        ]}
      />
    </VStack>
  );
}

export default TermsAgreementForm;
